import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, naira, Icon } from "../lib/core";
import { toast } from "sonner";
import { ContactsPage } from "./contacts";

const RELATIONSHIPS = ["Child", "Spouse", "Parent", "Sibling", "Ward", "Family"];
const PROVIDER_COLORS = { MTN: "#FFCC00", Airtel: "#E11D2E", Glo: "#22C55E", "9mobile": "#0F9D58" };
const initials = (n) => (n || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
const ordinal = (d) => { const s = ["th", "st", "nd", "rd"], v = d % 100; return d + (s[(v - 20) % 10] || s[v] || s[0]); };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return "—"; } };

// ============================================================
// Add / Edit Member modal
// ============================================================
const MemberModal = ({ open, member, dataPlans = {}, onClose, onSaved }) => {
  const editing = !!member;
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [relationship, setRelationship] = useState("Child");
  const [service, setService] = useState("airtime");
  const [amount, setAmount] = useState("");
  const [subcode, setSubcode] = useState("");
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [frequency, setFrequency] = useState("monthly");
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [timeOfDay, setTimeOfDay] = useState("09:00");
  const [provider, setProvider] = useState("MTN");
  const [providerTouched, setProviderTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    if (open) {
      setName(member?.name || "");
      setPhone(member?.phone || "");
      setRelationship(member?.relationship || "Child");
      setService(member?.service || "airtime");
      setAmount(member?.amount ? String(member.amount) : "");
      setSubcode(member?.subcode || "");
      setDayOfMonth(member?.day_of_month || 1);
      setFrequency(member?.frequency || "monthly");
      setDayOfWeek(member?.day_of_week ?? 0);
      setTimeOfDay(member?.time_of_day || "09:00");
      setProvider(member?.provider || "MTN");
      setProviderTouched(!!member);  // keep an existing member's network unless the user changes it
    }
  }, [open, member]);

  const detectProvider = (p) => {
    const d = (p || "").replace(/\D/g, "");
    const key = d.startsWith("234") ? "0" + d.slice(3, 6) : d.slice(0, 4);
    const map = { "0803": "MTN", "0806": "MTN", "0703": "MTN", "0706": "MTN", "0813": "MTN", "0816": "MTN", "0810": "MTN", "0814": "MTN", "0903": "MTN", "0906": "MTN", "0913": "MTN", "0916": "MTN", "0802": "Airtel", "0808": "Airtel", "0708": "Airtel", "0812": "Airtel", "0701": "Airtel", "0902": "Airtel", "0907": "Airtel", "0901": "Airtel", "0904": "Airtel", "0805": "Glo", "0807": "Glo", "0705": "Glo", "0815": "Glo", "0811": "Glo", "0905": "Glo", "0915": "Glo", "0809": "9mobile", "0817": "9mobile", "0818": "9mobile", "0908": "9mobile", "0909": "9mobile" };
    return map[key];
  };

  // Suggest a network from the phone prefix only until the user manually picks one
  // (so a ported/changed number can be overridden and edits aren't reset).
  useEffect(() => { if (providerTouched) return; const p = detectProvider(phone); if (p) setProvider(p); }, [phone, providerTouched]);

  const validityDays = (desc) => {
    const s = (desc || "").toLowerCase();
    if (/\bdaily\b|\b1\s*day\b/.test(s)) return 1;
    const m = s.match(/(\d+)\s*(day|week|month|year)/);
    if (m) { const n = +m[1]; const u = m[2]; return u === "day" ? n : u === "week" ? n * 7 : u === "month" ? n * 30 : n * 365; }
    if (/\bweekly\b/.test(s)) return 7;
    if (/\bmonthly\b/.test(s)) return 30;
    return 999;
  };
  const plans = [...(dataPlans[provider] || [])].sort(
    (a, b) => (validityDays(a.description) - validityDays(b.description)) || ((a.amount || 0) - (b.amount || 0))
  );
  const selectedPlan = plans.find((p) => p.subCode === subcode);

  const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const _h24 = parseInt((timeOfDay || "09:00").split(":")[0], 10) || 0;
  const _min = (timeOfDay || "09:00").split(":")[1] || "00";
  const _ampm = _h24 >= 12 ? "PM" : "AM";
  const _h12 = _h24 % 12 || 12;
  const setTime = (h12, min, ap) => {
    const h = (h12 % 12) + (ap === "PM" ? 12 : 0);
    setTimeOfDay(`${String(h).padStart(2, "0")}:${min}`);
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Enter a name");
    if (phone.replace(/\D/g, "").length < 10) return toast.error("Enter a valid phone number");
    if (service === "airtime" && (!amount || Number(amount) < 50)) return toast.error("Airtime allowance must be at least ₦50");
    if (service === "data" && !subcode) return toast.error("Choose a data plan");
    setSaving(true);
    const body = {
      name: name.trim(), phone, provider, relationship, service,
      amount: service === "data" ? (selectedPlan?.amount || 0) : Number(amount),
      subcode: service === "data" ? subcode : null,
      plan_label: service === "data" ? (selectedPlan?.description || null) : null,
      frequency, day_of_week: Number(dayOfWeek), day_of_month: Number(dayOfMonth),
      time_of_day: timeOfDay, is_active: member ? member.is_active : true,
    };
    try {
      if (editing) await api.put(`/family/members/${member.id}`, body);
      else await api.post("/family/members", body);
      toast.success(editing ? "Member updated" : "Member added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save member");
    } finally { setSaving(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-end sm:items-center sm:justify-center p-0 sm:p-4" onClick={onClose}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            onClick={(e) => e.stopPropagation()} data-testid="family-member-modal"
            className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-extrabold text-slate-900">{editing ? "Edit member" : "Add family member"}</h3>
              <button onClick={onClose} data-testid="family-modal-close" className="text-slate-400 hover:text-slate-700"><Icon name="close" /></button>
            </div>

            {!editing && (
              <button type="button" onClick={() => setShowPicker(true)} data-testid="family-pick-contact"
                className="w-full mb-4 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-red-300 text-red-600 font-semibold text-sm hover:bg-red-50 transition-colors">
                <Icon name="contacts" className="text-base" /> Pick from Contacts
              </button>
            )}

            <label className="block text-xs font-semibold text-slate-500 mb-1">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="family-name-input" placeholder="e.g. Junior"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm mb-3 outline-none focus:border-red-500" />

            <label className="block text-xs font-semibold text-slate-500 mb-1">Phone number</label>
            <div className="relative mb-3">
              <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="family-phone-input" placeholder="080..." inputMode="tel"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-500" />
            </div>

            <label className="block text-xs font-semibold text-slate-500 mb-1">Network {providerTouched ? "" : "(auto-detected — change if the number was ported)"}</label>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {["MTN", "Airtel", "Glo", "9mobile"].map((net) => (
                <button key={net} type="button" onClick={() => { setProvider(net); setProviderTouched(true); if (service === "data") setSubcode(""); }} data-testid={`family-net-${net}`}
                  className={`text-xs font-bold py-2 rounded-xl border transition-colors ${provider === net ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-200"}`}
                  style={provider === net ? { background: PROVIDER_COLORS[net] || "#334155" } : {}}>{net}</button>
              ))}
            </div>

            <label className="block text-xs font-semibold text-slate-500 mb-1">Relationship</label>
            <div className="flex flex-wrap gap-2 mb-4">
              {RELATIONSHIPS.map((r) => (
                <button key={r} onClick={() => setRelationship(r)} data-testid={`family-rel-${r}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${relationship === r ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>{r}</button>
              ))}
            </div>

            <label className="block text-xs font-semibold text-slate-500 mb-1">Monthly allowance</label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button onClick={() => setService("airtime")} data-testid="family-service-airtime"
                className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold border ${service === "airtime" ? "bg-red-50 border-red-500 text-red-600" : "bg-white border-slate-200 text-slate-500"}`}>
                <Icon name="phone_in_talk" className="text-base" />Airtime</button>
              <button onClick={() => setService("data")} data-testid="family-service-data"
                className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold border ${service === "data" ? "bg-red-50 border-red-500 text-red-600" : "bg-white border-slate-200 text-slate-500"}`}>
                <Icon name="wifi" className="text-base" />Data</button>
            </div>

            {service === "airtime" ? (
              <div className="mb-4">
                <div className="flex flex-wrap gap-2 mb-2">
                  {[200, 500, 1000, 2000].map((a) => (
                    <button key={a} onClick={() => setAmount(String(a))} data-testid={`family-amt-${a}`}
                      className={`text-xs font-bold px-3 py-1.5 rounded-lg border ${Number(amount) === a ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>{naira(a)}</button>
                  ))}
                </div>
                <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} data-testid="family-amount-input" placeholder="Custom amount ₦" inputMode="numeric"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-500" />
              </div>
            ) : (
              <div className="mb-4">
                <select value={subcode} onChange={(e) => setSubcode(e.target.value)} data-testid="family-plan-select"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-500">
                  <option value="">{plans.length ? "Select a data plan" : "No plans for this network"}</option>
                  {plans.map((p) => <option key={p.subCode} value={p.subCode}>{p.description} — {naira(p.amount)}</option>)}
                </select>
                {selectedPlan && <p className="text-xs text-slate-500 mt-1.5">Charged {naira(selectedPlan.amount)} from your wallet each cycle.</p>}
              </div>
            )}

            <label className="block text-xs font-semibold text-slate-500 mb-1">How often?</label>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]].map(([v, lbl]) => (
                <button key={v} type="button" onClick={() => setFrequency(v)} data-testid={`family-freq-${v}`}
                  className={`text-xs font-bold py-2.5 rounded-xl border transition-colors ${frequency === v ? "bg-red-600 text-white border-transparent" : "bg-white text-slate-600 border-slate-200"}`}>{lbl}</button>
              ))}
            </div>

            {frequency === "monthly" && (
              <div className="mb-3">
                <label className="block text-xs font-semibold text-slate-500 mb-1">Charge day (each month)</label>
                <select value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)} data-testid="family-day-select"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-500">
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{ordinal(d)} of every month</option>)}
                </select>
              </div>
            )}
            {frequency === "weekly" && (
              <div className="mb-3">
                <label className="block text-xs font-semibold text-slate-500 mb-1">Charge day (each week)</label>
                <select value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)} data-testid="family-weekday-select"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-500">
                  {WEEKDAYS.map((d, i) => <option key={i} value={i}>Every {d}</option>)}
                </select>
              </div>
            )}

            <label className="block text-xs font-semibold text-slate-500 mb-1">Time of day</label>
            <div className="grid grid-cols-3 gap-2 mb-5" data-testid="family-time-picker">
              <select value={_h12} onChange={(e) => setTime(Number(e.target.value), _min, _ampm)} data-testid="family-hour"
                className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-red-500">
                {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
              <select value={_min} onChange={(e) => setTime(_h12, e.target.value, _ampm)} data-testid="family-minute"
                className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-red-500">
                {["00", "15", "30", "45"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={_ampm} onChange={(e) => setTime(_h12, _min, e.target.value)} data-testid="family-ampm"
                className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-red-500">
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>

            <button onClick={save} disabled={saving} data-testid="family-save-member"
              className="w-full bg-gradient-to-r from-red-600 to-red-500 text-white font-bold py-3.5 rounded-xl disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Save changes" : "Add member"}
            </button>

            {showPicker && (
              <div className="fixed inset-0 z-[90] overflow-y-auto bg-background-light" data-testid="family-contact-picker">
                <ContactsPage
                  pickMode
                  onBack={() => setShowPicker(false)}
                  onPick={(c) => {
                    setName(c.name || "");
                    setPhone(c.phone || "");
                    if (c.provider) { setProvider(c.provider); setProviderTouched(true); }
                    setShowPicker(false);
                  }}
                />
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ============================================================
// Family page
// ============================================================
export const FamilyPage = ({ dataPlans = {}, onWalletChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [history, setHistory] = useState([]);
  const [spend, setSpend] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/family");
      setData(r.data);
    } catch (e) { /* noop */ } finally { setLoading(false); }
    api.get("/family/history").then((r) => setHistory(r.data.items || [])).catch(() => {});
    api.get("/family/spend-summary").then((r) => setSpend(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (m) => {
    setBusyId(m.id);
    try {
      await api.put(`/family/members/${m.id}`, { ...m, is_active: !m.is_active });
      await load();
    } catch (e) { toast.error("Could not update"); } finally { setBusyId(null); }
  };

  const topUpNow = async (m) => {
    setBusyId(m.id);
    try {
      await api.post(`/family/members/${m.id}/topup-now`);
      toast.success(`Allowance sent to ${m.name} 🎉`);
      onWalletChanged && onWalletChanged();
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Top-up failed"); } finally { setBusyId(null); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from your family?`)) return;
    setBusyId(m.id);
    try { await api.delete(`/family/members/${m.id}`); toast.success("Member removed"); await load(); }
    catch (e) { toast.error("Could not remove"); } finally { setBusyId(null); }
  };

  const members = data?.members || [];
  const s = data?.summary || {};

  return (
    <div className="max-w-4xl space-y-6" data-testid="family-view">
      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0B1220] to-[#1c1030] border border-white/10 p-6 text-white">
        <div className="absolute -right-8 -top-8 w-40 h-40 bg-red-500/20 rounded-full blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-10 h-10 rounded-2xl bg-gradient-to-br from-red-500 to-fuchsia-600 flex items-center justify-center"><Icon name="diversity_3" className="text-white" /></span>
            <div>
              <p className="text-white font-extrabold text-lg leading-none">{data?.family?.name || "My Family"}</p>
              <p className="text-slate-400 text-xs mt-1">Auto top-ups from your wallet, every month</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-5">
            <div><p className="text-slate-400 text-[11px]">Wallet balance</p><p className="font-extrabold text-base" data-testid="family-wallet-balance">{naira(s.wallet_balance || 0)}</p></div>
            <div><p className="text-slate-400 text-[11px]">Monthly commitment</p><p className="font-extrabold text-base" data-testid="family-monthly">{naira(s.monthly_commitment || 0)}</p></div>
            <div><p className="text-slate-400 text-[11px]">Members</p><p className="font-extrabold text-base">{s.active_count || 0}/{s.member_count || 0} active</p></div>
          </div>
          {s.monthly_commitment > s.wallet_balance && (s.active_count > 0) && (
            <div className="mt-4 flex items-center gap-2 bg-amber-400/15 border border-amber-400/30 rounded-xl px-3 py-2" data-testid="family-lowbalance-warning">
              <Icon name="warning" className="text-amber-300 text-base" />
              <p className="text-amber-100 text-xs font-medium">Your wallet is below this month's commitment. Top up to avoid skipped allowances.</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Monthly spend summary */}
      {spend && spend.this_month > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl border border-slate-200/70 p-5" data-testid="family-spend-summary">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-bold text-slate-900">Family spend · {spend.month_label}</p>
              <p className="text-xs text-slate-400">Successful allowance top-ups this month</p>
            </div>
            <div className="text-right">
              <p className="font-extrabold text-slate-900 text-lg" data-testid="family-spend-this">{naira(spend.this_month)}</p>
              {spend.delta_pct != null && (
                <p className={`text-xs font-semibold ${spend.delta_pct >= 0 ? "text-red-500" : "text-emerald-600"}`}>
                  {spend.delta_pct >= 0 ? "▲" : "▼"} {Math.abs(spend.delta_pct)}% vs last month ({naira(spend.last_month)})
                </p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {spend.per_member.map((m) => {
              const pct = spend.this_month ? Math.round((m.amount / spend.this_month) * 100) : 0;
              return (
                <div key={m.member_id} data-testid={`family-spend-member-${m.member_id}`}>
                  <div className="flex items-center justify-between text-sm mb-0.5">
                    <span className="text-slate-700 font-medium truncate">{m.member_name} <span className="text-slate-400 text-xs">· {m.count}×</span></span>
                    <span className="font-bold text-slate-900">{naira(m.amount)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-gradient-to-r from-red-500 to-fuchsia-500" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="font-bold text-slate-900 text-lg">Members</h2>
        <button onClick={() => { setEditing(null); setModalOpen(true); }} data-testid="family-add-member"
          className="flex items-center gap-1.5 bg-slate-900 text-white text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-slate-800">
          <Icon name="person_add" className="text-base" />Add member</button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : members.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-slate-200/70" data-testid="family-empty">
          <Icon name="diversity_3" className="text-5xl text-slate-300" />
          <p className="mt-3 text-slate-600 font-semibold">No family members yet</p>
          <p className="text-sm text-slate-400 mt-1">Add a member and set a monthly allowance — we'll top them up automatically.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {members.map((m) => (
            <motion.div key={m.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl border border-slate-200/70 p-5" data-testid={`family-member-${m.id}`}>
              <div className="flex items-start gap-3">
                <span className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center font-extrabold text-white" style={{ background: PROVIDER_COLORS[m.provider] || "#334155" }}>{initials(m.name)}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-slate-900 truncate">{m.name}</p>
                    <span className="text-[10px] font-semibold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{m.relationship}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{m.phone} · {m.provider}</p>
                </div>
                <button onClick={() => toggleActive(m)} disabled={busyId === m.id} data-testid={`family-toggle-${m.id}`}
                  className={`relative w-11 h-6 rounded-full transition-colors ${m.is_active ? "bg-emerald-500" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${m.is_active ? "left-0.5 translate-x-[20px]" : "left-0.5"}`} />
                </button>
              </div>

              <div className="mt-4 rounded-2xl bg-slate-50 p-3.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon name={m.service === "data" ? "wifi" : "phone_in_talk"} className="text-red-500 text-lg" />
                    <div>
                      <p className="font-extrabold text-slate-900 text-sm">{m.service === "data" ? (m.plan_label || "Data plan") : `${naira(m.amount)} airtime`}</p>
                      <p className="text-[11px] text-slate-500">{m.frequency === "daily" ? "Daily" : m.frequency === "weekly" ? `Weekly · ${["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][m.day_of_week || 0]}` : `Monthly · ${ordinal(m.day_of_month)}`} · next {fmtDate(m.next_execution)}</p>
                    </div>
                  </div>
                  <p className="font-extrabold text-slate-900 text-sm">{naira(m.amount)}</p>
                </div>
              </div>

              <div className="flex gap-2 mt-3">
                <button onClick={() => topUpNow(m)} disabled={busyId === m.id} data-testid={`family-topup-${m.id}`}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-bold py-2.5 rounded-xl disabled:opacity-60">
                  {busyId === m.id ? "…" : "Top up now"}</button>
                <button onClick={() => { setEditing(m); setModalOpen(true); }} data-testid={`family-edit-${m.id}`}
                  className="px-3 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200"><Icon name="edit" className="text-base" /></button>
                <button onClick={() => remove(m)} data-testid={`family-delete-${m.id}`}
                  className="px-3 bg-red-50 text-red-500 rounded-xl hover:bg-red-100"><Icon name="delete" className="text-base" /></button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div>
          <h2 className="font-bold text-slate-900 text-lg mb-3">Allowance history</h2>
          <div className="bg-white rounded-3xl border border-slate-200/70 divide-y divide-slate-100" data-testid="family-history">
            {history.slice(0, 10).map((h) => (
              <div key={h.id} className="flex items-center gap-3 px-4 py-3">
                <span className={`w-8 h-8 rounded-full flex items-center justify-center ${h.status === "success" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
                  <Icon name={h.status === "success" ? "check" : "close"} className="text-base" /></span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{h.member_name} · {naira(h.amount)} {h.service}</p>
                  <p className="text-[11px] text-slate-400">{fmtDate(h.created_at)} · {h.kind === "auto" ? "Auto" : "Manual"}{h.status !== "success" && h.message ? ` · ${h.message}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <MemberModal open={modalOpen} member={editing} dataPlans={dataPlans}
        onClose={() => setModalOpen(false)} onSaved={() => { load(); onWalletChanged && onWalletChanged(); }} />
    </div>
  );
};
